import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "reduxStore/state";
import * as Yup from "yup";
import {
  createAssessment,
  updateAssessment,
} from "reduxStore/actions/assessmentActions";
import { useState } from "react";
import { debounce } from "lodash";
import { searchDivisions } from "reduxStore/actions/divisionActions";
import { fetchActivePrincipleQuestions } from "reduxStore/actions/principleQuestionsActions";
import { useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import { fetchLevelsForLocation } from "reduxStore/actions/levelActions";
import { getPermissionFlags, getUserScope } from "utils/permissions";
import dayjs from "dayjs";

export const useAssessmentForm = ({ editMode, currentAssessment, setOpen }) => {
  const dispatch = useDispatch();

  const isLargeScreen = useMediaQuery("(min-width:1000px)");

  const divisionSearchResults = useSelector(
    (state) => state.divisionSearchResults
  );

  const principleQuestions = useSelector(
    (state) => state.principleQuestions.data
  );

  const [valuesLocation, setValuesLocation] = useState(null);
  const levelsForLocation = useSelector((state) => state.levels.data);

  const [searchDivisionTerm, setSearchDivisionTerm] = useState("");
  const user = useSelector((state) => state.user);
  const { organisationId, locationIds, divisionIds } = getUserScope(user);
  const location = locationIds[0] || "";
  const divisionId = divisionIds[0] || "";

  const { showOrg } = getPermissionFlags(user);

  // Validation Schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Assessee’s Name is required"),
    assessee: Yup.mixed().nullable(),
    assesseeEmail: Yup.string()
      .email("Please enter a valid email")
      .when("assessee", {
        is: (value) => !value,
        then: (schema) => schema.required("Assessee’s Email is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    dateOfAssessment: Yup.date().required("Date of Assessment is required"),
    lessonPeriod: Yup.string().required("Lesson Period is required"),
    division: Yup.object().required("Division is required"),
    level: Yup.object().required("Year is required"),
    location: Yup.object().nullable().required("Location is required"),
    principleQuestions: Yup.array()
      .of(
        Yup.object().shape({
          question: Yup.string().required("Question is required"),
          principle: Yup.string().required("Principle is required"),
          areaOfStrength: Yup.boolean().required(
            "Area of Strength is required"
          ),
        })
      )
      .test(
        "valid-questions",
        "You must have 1-3 strengths and up to 3 areas for development.",
        (principleQuestions) => {
          if (!principleQuestions || principleQuestions.length === 0)
            return false;

          const strengths = principleQuestions.filter(
            (q) => q.areaOfStrength
          ).length;
          const areasForDevelopment = principleQuestions.length - strengths;

          return strengths >= 1 && strengths <= 3 && areasForDevelopment <= 3;
        }
      ),
    notes: Yup.string().max(1000, "Notes cannot exceed 1000 characters"),
  });

  // Set Initial Values
  const initialValues = {
    name: currentAssessment?.name || "",
    assessee:
      (currentAssessment?.assessee &&
        (currentAssessment.assessee._id || currentAssessment.assessee)) ||
      null,
    assesseeEmail:
      (currentAssessment?.assessee &&
        currentAssessment.assessee.email &&
        currentAssessment.assessee.email) ||
      "",
    dateOfAssessment: currentAssessment
      ? dayjs(currentAssessment.dateOfAssessment).format("YYYY-MM-DD")
      : "",
    lessonPeriod: currentAssessment ? currentAssessment.lessonPeriod : "",
    division: currentAssessment?.division
      ? { ...currentAssessment.division, location: currentAssessment.location }
      : divisionId && divisionSearchResults.length > 0
        ? divisionSearchResults.find((d) => d._id === divisionId) || null
        : null,
    level: currentAssessment?.level || {},
    location:
      currentAssessment?.location ||
      (location ? { _id: location } : null),
    principleQuestions: currentAssessment
      ? currentAssessment.principleQuestions.map((pq) => ({
          question:
            typeof pq.question === "object" && pq.question !== null
              ? pq.question._id
              : pq.question,
          principle:
            typeof pq.principle === "object" && pq.principle !== null
              ? pq.principle._id
              : pq.principle,
          areaOfStrength: pq.areaOfStrength,
        }))
      : [],
    notes: currentAssessment ? currentAssessment.notes : "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    values.organisation = organisationId;
    values.location = values.location?._id || values.location || null;
    values.division = values.division?._id || values.division || null;
    values.level = values.level?._id || values.level || null;
    values.assessee =
      (values.assessee && values.assessee._id) || values.assessee || null;
    values.assesseeEmail = values.assesseeEmail
      ? values.assesseeEmail.trim().toLowerCase()
      : "";
    values.name = values.name ? values.name.trim() : "";
    values.assessor = editMode ? currentAssessment.assessor._id : user._id;
    try {
      let response;
      if (editMode) {
        response = await dispatch(
          updateAssessment(currentAssessment._id, values)
        );
      } else {
        response = await dispatch(createAssessment(values));
      }

      if (response && !response.error) {
        dispatch(
          setSnackbar({
            open: true,
            message: editMode
              ? "Assessment updated successfully"
              : "Assessment created successfully",
            severity: "success",
          })
        );
      } else {
        dispatch(
          setSnackbar({
            open: true,
            message: response.error || "An error occurred",
            severity: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        setSnackbar({
          open: true,
          message: error.message,
          severity: "error",
        })
      );
    } finally {
      setSubmitting(false);
      setOpen(false);
      resetForm();
    }
  };

  // Debounce the search term to prevent too many API calls
  useEffect(() => {
    const debounceFetch = debounce(() => {
      if (searchDivisionTerm) {
        if (!showOrg) {
          dispatch(searchDivisions({ search: searchDivisionTerm, location }));
        } else {
          dispatch(searchDivisions({ search: searchDivisionTerm }));
        }
      }
    }, 300); // 300ms delay
    debounceFetch();

    // Cleanup function to cancel the debounce on unmount
    return () => {
      debounceFetch.cancel();
    };
  }, [searchDivisionTerm, dispatch, location, showOrg]);

  useEffect(() => {
    if (valuesLocation && valuesLocation._id) {
      const locationId =
        typeof valuesLocation._id === "object"
          ? valuesLocation._id._id // nested case
          : valuesLocation._id; // plain string

      if (locationId) {
        dispatch(fetchLevelsForLocation(locationId));
        dispatch(fetchActivePrincipleQuestions(locationId));
      }
    }
  }, [valuesLocation, dispatch]);

  return {
    validationSchema,
    initialValues,
    handleSubmit,
    setSearchDivisionTerm,
    divisionSearchResults,
    principleQuestions,
    isLargeScreen,
    setValuesLocation,
    levelsForLocation,
  };
};
